import React from 'react';
import Icon from '@/components/ui/AppIcon';
import type { Lesson } from '@/lib/mockData';

interface Props { lesson: Lesson; }

const PLAN_STEPS = [
  {
    id: 'step-1',
    phase: 'Hook',
    time: '10 min',
    title: 'Opening Discussion: What Is Climate Justice?',
    description:
      'Begin with a think-pair-share using the prompt: "Who do you think is most affected by extreme weather events?" Show the 2-minute BBC News clip on flooding in Bangladesh. Record student responses on the board.',
    materials: ['Whiteboard', 'BBC News clip (linked in resources)'],
    icon: 'LightBulbIcon',
    color: 'bg-primary-50 border-primary-200 text-primary-700',
  },
  {
    id: 'step-2',
    phase: 'Input',
    time: '20 min',
    title: 'Reading & Data Analysis: Climate Vulnerability Index',
    description:
      'Students read the Climate Vulnerability Index handout in pairs. Guide them to highlight: (1) which countries score highest on vulnerability, (2) which score highest on responsibility. Use the annotation protocol cards.',
    materials: ['Climate Vulnerability Index handout', 'Annotation protocol cards'],
    icon: 'DocumentMagnifyingGlassIcon',
    color: 'bg-accent-50 border-accent-200 text-accent-700',
  },
  {
    id: 'step-3',
    phase: 'Guided Practice',
    time: '25 min',
    title: 'Case Study Jigsaw: Bangladesh, Maldives, Louisiana',
    description:
      'Divide class into three groups, each studying one case. Groups analyze: local climate impacts, community responses, and government policy. Regroup in mixed triads to share findings using the jigsaw protocol.',
    materials: ['Case study packets (3 versions)', 'Jigsaw discussion cards'],
    icon: 'UsersIcon',
    color: 'bg-terracotta-50 border-terracotta-200 text-terracotta-700',
  },
  {
    id: 'step-4',
    phase: 'Discussion',
    time: '15 min',
    title: 'Socratic Seminar: Who Is Responsible?',
    description:
      'Full-class Socratic seminar on the essential question. Use the discussion sentence starters provided. Teacher facilitates without leading — track participation on the class roster sheet.',
    materials: ['Discussion sentence starters', 'Participation tracker'],
    icon: 'ChatBubbleLeftRightIcon',
    color: 'bg-blue-50 border-blue-200 text-blue-700',
  },
  {
    id: 'step-5',
    phase: 'Closure',
    time: '10 min',
    title: 'Exit Ticket: One Change, One Question',
    description:
      'Students complete the exit ticket: (1) One thing they learned that changed their thinking, (2) One question they still have. Collect for formative assessment before next class.',
    materials: ['Exit ticket slips'],
    icon: 'PencilSquareIcon',
    color: 'bg-amber-50 border-amber-200 text-amber-700',
  },
];

export default function LessonPlanTab({ lesson }: Props) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-semibold text-stone-800 text-lg">Step-by-Step Lesson Plan</h2>
        <span className="text-sm text-stone-500 bg-stone-100 px-3 py-1 rounded-full">
          Total: {lesson.duration} · 80 min/day
        </span>
      </div>

      <div className="space-y-4">
        {PLAN_STEPS.map((step, i) => (
          <div
            key={step.id}
            className={`rounded-2xl border p-5 ${step.color}`}
          >
            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center gap-1 flex-shrink-0">
                <div className="w-9 h-9 rounded-xl bg-white/60 flex items-center justify-center">
                  <Icon name={step.icon as any} size={18} />
                </div>
                <span className="text-xs font-bold opacity-60">0{i + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className="text-xs font-bold uppercase tracking-wider opacity-70">
                    {step.phase}
                  </span>
                  <span className="text-xs font-medium bg-white/60 px-2 py-0.5 rounded-full">
                    {step.time}
                  </span>
                </div>
                <h4 className="font-semibold text-sm mb-2">{step.title}</h4>
                <p className="text-sm opacity-80 leading-relaxed mb-3">{step.description}</p>
                {step.materials.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {step.materials.map((mat) => (
                      <span
                        key={`mat-${step.id}-${mat}`}
                        className="text-xs px-2 py-0.5 bg-white/50 rounded-full border border-current/20"
                      >
                        {mat}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Discussion prompts */}
      <div className="mt-8 bg-stone-50 rounded-2xl p-6 border border-stone-200">
        <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
          <Icon name="ChatBubbleBottomCenterTextIcon" size={18} className="text-accent-600" />
          Discussion Prompts
        </h3>
        <ul className="space-y-3">
          {[
            'How is climate change experienced differently depending on where you live and how much money your country has?',
            'Who has the most responsibility to address climate change — individuals, corporations, or governments? Why?',
            'What would climate justice look like in practice? Give a specific example.',
            'How does your own community\'s carbon footprint connect to climate impacts in Bangladesh or the Maldives?',
          ].map((prompt, i) => (
            <li key={`prompt-${i}`} className="flex items-start gap-3 text-sm text-stone-700">
              <span className="mt-0.5 w-5 h-5 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                Q
              </span>
              {prompt}
            </li>
          ))}
        </ul>
      </div>

      {/* Extensions */}
      <div className="mt-6 bg-white rounded-2xl p-6 border border-stone-200">
        <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
          <Icon name="ArrowTrendingUpIcon" size={18} className="text-primary-600" />
          Extension Activities
        </h3>
        <ul className="space-y-2">
          {[
            'Research and present on a local climate justice organization in your community',
            'Write a letter to an elected official advocating for a specific climate policy',
            'Create an infographic comparing emissions and vulnerability for 5 countries',
            'Interview a community member about how climate change has affected their life',
          ].map((ext, i) => (
            <li key={`ext-${i}`} className="flex items-start gap-2.5 text-sm text-stone-700">
              <Icon name="PlusCircleIcon" size={15} className="text-primary-400 flex-shrink-0 mt-0.5" />
              {ext}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}